import { colors } from "@/theme";
import styled from "@emotion/styled";
import moment from "moment";
import type React from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  AttributeIcon,
  type GraphCounts,
  GraphNodeType,
  getGraphNodeTypeColor,
  getNodeType,
  scrollable,
  useGraphStatistics,
} from "@/common";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { Label } from "@/components/meta";

import Icon from "@imminently/decisively-ui/Icon";
import { Tooltip, Typography } from "@mui/material";
import type { GraphNode } from "@packages/commons";
import clsx from "clsx";

type StatChipProps = {
  label: ReactNode;
  value: number;
  color: string;
};

const StatChip = ({ label, value, color }: StatChipProps) => (
  <Stat color={color}>
    <span>{label}</span>
    <Count>{value}</Count>
  </Stat>
);

export const GraphStatisticsBar = ({
  counts,
  className,
  ...props
}: { counts: GraphCounts } & React.HTMLAttributes<HTMLDivElement>) => {
  const { t } = useTranslation();
  return (
    <div
      className={clsx("flex items-center gap-2", className)}
      {...props}
    >
      <StatChip
        label={<span style={{ color: "white" }}>{t("debugger.stats_total_attributes")}</span>}
        value={counts.total}
        color={colors.kindaBlack}
      />
      <StatChip
        label={t("debugger.stats_inputs")}
        value={counts.input}
        color={getGraphNodeTypeColor(GraphNodeType.INPUT)}
      />
      <StatChip
        label={t("debugger.stats_derived")}
        value={counts.derived}
        color={getGraphNodeTypeColor(GraphNodeType.DERIVED)}
      />
      <StatChip
        label={t("debugger.stats_goals")}
        value={counts.goals}
        color={getGraphNodeTypeColor(GraphNodeType.GOAL)}
      />
    </div>
  );
};

const Statbar = styled(GraphStatisticsBar)`
  padding: 0.5rem 2rem;
  border-bottom: 1px solid var(--border);
  border-top: 1px solid var(--border);
`;

const Stat = styled(Label)`
  display: inline-flex;
  flex-flow: row nowrap;
  gap: 0.5rem;
  border-radius: 1rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--border);
`;

const Count = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-radius: 1rem;
  /* width: 1rem; */
  height: 1rem;
  padding: 0 0.25rem;
`;

const AttributeContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  align-items: center;
  border: 1px solid var(--border);
  cursor: pointer;

  &:hover {
    background-color: var(--secondary-hover);
  }

  #icon {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 2rem;
    border: 1px solid var(--border);
    padding: 0.5rem;
  }

  /* svg {
    width: 2rem;
    height: 2rem;
  } */
`;

const Attribute = ({ node, graph }: { node: GraphNode; graph: any }) => {
  const type = getNodeType(node) ?? GraphNodeType.INPUT; // default to input
  return (
    <AttributeContainer>
      <div
        id="icon"
        style={{ backgroundColor: getGraphNodeTypeColor(type) }}
      >
        <AttributeIcon type={node.type} />
      </div>
      <Typography>{node.description}</Typography>
    </AttributeContainer>
  );
};

export const GraphStatistics = ({ graph, debug = false }) => {
  const release = useFullRelease();
  const { t } = useTranslation();
  const { counts, interestingNodes } = useGraphStatistics(graph, debug);

  if (!graph) return <Typography>No graph available</Typography>;

  const lastUpdated = release ? moment(release.lastModified).format("DD/MM/YYYY - HH:mm:ss") : null;

  const helpTooltip = t("debugger.stats_help_tooltip");

  return (
    <div
      className="flex flex-col pt-4"
      style={{ gap: 0 }}
    >
      <Statbar counts={counts} />

      <div className="flex items-start pt-12 pb-2">
        <div className="flex flex-col flex-1 pl-8 gap-2">
          <div className="flex items-center gap-2">
            <Typography variant="h4">{t("debugger.important_nodes")}</Typography>
            <Tooltip title={helpTooltip}>
              <Icon
                name="help"
                style={{ width: "1rem", height: "1rem" }}
              />
            </Tooltip>
          </div>
          <Typography>{t("debugger.important_nodes_description")}</Typography>
        </div>

        <div className={`flex flex-col flex-1 pr-4 gap-2 max-h-[300px] ${scrollable}`}>
          {interestingNodes.map((node) => (
            <Attribute
              key={node.id}
              node={node}
              graph={graph}
            />
          ))}
        </div>
      </div>
      {lastUpdated ? (
        <div
          className="flex justify-end items-center gap-2"
          style={{
            color: colors.reallyGrey,
            padding: "0.5rem 2rem 1rem 2rem",
          }}
        >
          <Icon name="version-history" />
          <Typography variant="caption">
            {t("updated_at")}: {lastUpdated}
          </Typography>
        </div>
      ) : null}
    </div>
  );
};
